<template>
    <section class="resource-page__hero">
        <ResourcePageHeroShape />
        <div class="content">
            <Breadcrumbs />
            <CategoryPillList v-if="isValidDocumentType(document)" :document="document" class="resource-page__hero__category-pills" />

            <div v-if="isEventDocument(document) && document.data.partnershipLogo.url" class="resource-page__hero__partnership-logo">
                <PrismicImage :field="document.data.partnershipLogo" :alt="document.data.partnershipLogo.alt" />
            </div>
            <div
                :class="{
                    'document-info': true,
                    'document-info--isEventPage': isEventDocument(document),
                }"
            >
                <div class="content-info">
                    <h1 :class="{title: true, 'title--isNotEventPage': !isEventDocument(document)}">{{ document.data?.title }}</h1>

                    <div v-if="isArticleDocument(document) || isEventDocument(document)" class="time-info">
                        <template v-if="isEventDocument(document) && isFutureEvent">
                            <div class="time-info-event">
                                <div class="time-info-event__date">
                                    <MktDateTimeRange
                                        v-if="eventDocumentStartsDate"
                                        :starts-date="eventDocumentStartsDate"
                                        :ends-date="eventDocumentEndsDate"
                                    />
                                    <div v-else>
                                        <MktDate v-if="eventDocumentDate" :date="eventDocumentDate" />
                                    </div>
                                    <div v-if="isFutureEvent && document.data.eventType" class="time-info-event__event-type">
                                        <div>{{ document.data.eventType }}</div>
                                    </div>
                                </div>
                                <MktLocation v-if="location" :location="document.data.location" />
                            </div>
                        </template>

                        <template v-if="isArticleDocument(document)">
                            <div class="time-info-article">
                                <MktDate v-if="releaseDate" :date="releaseDate" phrase-key-with-date="resources.date.label.published" />
                                <MktDate v-if="lastUpdatedDate" :date="lastUpdatedDate" phrase-key-with-date="resources.date.label.updated" />
                                <div v-if="document.data?.duration && document.data.duration !== ''" class="time-info-article-duration">
                                    <VJoyIcon name="clock" :color="iconColor" />
                                    <span>{{ document.data?.duration.replace('minutes', 'mn') }}</span>
                                </div>
                            </div>
                        </template>
                    </div>
                    <PrismicRichText class="excerpt" :field="document.data?.excerpt" />

                    <div v-if="isEventDocument(document)" class="resource-page__hero__event-thumbnail">
                        <PrismicImage v-if="isFutureEvent && !replayWebUrl" :field="document.data?.eventImage" :alt="document.data?.eventImage.alt" />
                        <MktVideoPlayer v-else-if="replayWebUrl" v-bind="video(document, replayWebUrl)" />
                    </div>

                    <AuthorInfo class="author-info" />
                </div>

                <div v-if="!hideImage && !isEventDocument(document) && document.type !== 'collection'" class="thumbnail">
                    <div v-if="!isFilled.image(document.data?.thumbnail)" />
                    <PrismicImage v-else :class="{thumbnail: true, cropped: document.type === 'article'}" :field="document.data?.thumbnail" />
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {useVariants} from '@nuxt-marketing-module/composables/useVariants';
    import {isFilled} from '@prismicio/helpers';
    import type {DateField, TimestampField} from '@prismicio/types';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {ArticleDocument, CollectionDocument, EventDocument, GuideDocument, SuccessStoryDocument} from '@prismicio-types';
    import {MktVideoPlayer, MktLocation, MktDateTimeRange, MktDate} from '#components';
    import {usePrismicTypeGuards} from '#imports';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import AuthorInfo from '~/components/AuthorInfo.vue';
    import Breadcrumbs from '~/components/Breadcrumbs.vue';
    import CategoryPillList from '~/components/CategoryPillList.vue';
    import {createDateHelpers} from '~/composables/dateHelpers';
    import ResourcePageHeroShape from './ResourcePageHeroShape.vue';

    const props = defineProps<{
        document: ArticleDocument | GuideDocument | SuccessStoryDocument | CollectionDocument | EventDocument;
        hideImage?: boolean;
    }>();

    const {icon: iconColor} = useVariants();
    const {hasProperty} = usePrismicTypeGuards();
    const {isBefore, isSameDay} = createDateHelpers();

    // Computed values
    const eventDocumentDate = computed(() => (hasProperty(props.document.data, 'date') ? new Date(props.document.data?.date as string) : undefined));
    const eventDocumentStartsDate = computed(
        () => hasProperty(props.document.data, 'eventStarts') && new Date(props.document.data?.eventStarts as string),
    );
    const eventDocumentEndsDate = computed(() =>
        hasProperty(props.document.data, 'eventEnds') ? new Date(props.document.data?.eventEnds as string) : undefined,
    );

    function inFuture(date: DateField | TimestampField) {
        return isBefore(new Date(), new Date(date as string)) || isSameDay(new Date(), new Date(date as string));
    }

    const isFutureEvent = computed(() => {
        if (!isEventDocument(props.document)) return false;

        if (props.document.data.eventStarts) {
            return inFuture(props.document.data.eventEnds);
        }

        return inFuture(props.document.data.date);
    });

    const releaseDate = computed((): Date | undefined =>
        hasProperty(props.document.data, 'releaseDate') && props.document.data.releaseDate ? new Date(props.document.data!.releaseDate!) : undefined,
    );
    const lastUpdatedDate = computed((): Date | undefined =>
        hasProperty(props.document, 'last_publication_date') && props.document.last_publication_date
            ? new Date(props.document.last_publication_date)
            : undefined,
    );

    function isValidDocumentType(doc: typeof props.document): doc is ArticleDocument | GuideDocument | SuccessStoryDocument | EventDocument {
        return ['article', 'guide', 'successStory', 'event'].includes(doc.type);
    }

    function isEventDocument(doc: typeof props.document): doc is EventDocument {
        return doc.type === 'event';
    }

    function isArticleDocument(doc: typeof props.document): doc is ArticleDocument {
        return doc.type === 'article';
    }

    const location = computed(
        (): boolean => !!(props.document as EventDocument).data.eventType && (props.document as EventDocument).data.eventType === 'offline',
    );

    const replayWebUrl = computed(() => {
        if (!isEventDocument(props.document)) return '';
        const rl = props.document.data.replayLink;
        return isFilled.link(rl) && rl.link_type === 'Web' ? rl.url : '';
    });

    function video(document: EventDocument, replayUrl: string) {
        const videoId = replayUrl.match(/[?&]v=([^&]+)/)?.[1] || replayUrl.match(/youtu\.be\/([^?]+)/)?.[1];
        const src = `https://www.youtube.com/embed/${videoId}?feature=oembed`;
        const title = document.data.coverText.map((node) => (hasProperty(node, 'text') ? node.text : '')).join('');
        const cover = document.data.coverImage?.url || '';

        return {src, title, cover};
    }
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .resource-page__hero {
        position: relative;
        padding: var(--joy-core-spacing-8) 0 80px;
    }

    /* Override category pills for theme strategy */
    .resource-page--strategy {
        .category-pill a {
            color: var(--joy-color-secondary-50);
            user-select: none;

            &:not(:hover)::after {
                background-color: var(--joy-color-neutral-0);
                border-color: var(--joy-color-secondary-50);
            }

            &:hover {
                color: var(--joy-color-neutral-0);

                &::after {
                    background-color: var(--joy-color-secondary-50);
                    border-color: var(--joy-color-secondary-50);
                }
            }
        }
    }

    /* Legacy css code */
    .resource-page__hero {
        &__category-pills {
            margin-top: var(--joy-core-spacing-6);
        }

        &__partnership-logo {
            margin-top: var(--joy-core-spacing-8);

            img {
                object-fit: contain;
                height: 60px;

                @include mq.screen_sm() {
                    height: 30px;
                }
            }
        }

        &__event-thumbnail {
            position: relative;
            top: 30px;

            img {
                margin-top: var(--joy-core-spacing-4);
                width: 100%;
                height: inherit;
                object-fit: contain;
                object-position: center;
            }
        }

        @media (max-width: $mobile-breakpoint) {
            padding: var(--joy-core-spacing-5) 0 var(--joy-core-spacing-9);
            background-size: 100% auto;
        }

        .content {
            position: relative;
            @include content-width;

            .document-info {
                margin-top: var(--joy-core-spacing-5);
                display: grid;
                grid-template-columns: 1fr 1fr;
                column-gap: var(--joy-core-spacing-12);

                &--isEventPage {
                    grid-template-columns: 60% 1fr;
                }

                @include mq.screen_md() {
                    &--isEventPage {
                        margin-right: 120px;
                    }
                }

                @media (max-width: $mobile-breakpoint) {
                    display: flex;
                    flex-direction: column;
                    margin-top: var(--joy-core-spacing-5);

                    &--isEventPage {
                        margin-right: 0;
                    }
                }

                .category-pill a:not(:hover)::after {
                    background-color: var(--joy-color-quaternary-10);
                }

                .thumbnail {
                    img {
                        max-width: 100%;
                        max-height: 100%;
                        margin: auto;
                        object-fit: contain;
                        border-radius: var(--joy-core-radius-4);
                    }

                    &.cropped {
                        aspect-ratio: 3 / 2;
                        object-fit: cover;
                        object-position: top;
                    }

                    @media (max-width: $mobile-breakpoint) {
                        width: 100%;
                        margin-bottom: var(--joy-core-spacing-4);
                        order: -1;
                        img {
                            max-height: min(70vw, 400px);
                        }
                    }
                }

                .title {
                    margin-top: var(--joy-core-spacing-4);
                    margin-bottom: var(--joy-core-spacing-8);

                    &--isNotEventPage {
                        font-size: 45px;
                    }

                    @media (max-width: $mobile-breakpoint) {
                        font-size: var(--joy-font-size-secondary-200);
                        margin-bottom: var(--joy-core-spacing-5);
                    }
                }

                .time-info {
                    display: flex;
                    align-items: center;
                    color: var(--joy-color-neutral-40);

                    .icon {
                        margin-right: var(--joy-core-spacing-2);
                    }

                    &-article {
                        display: flex;
                        align-items: flex-start;
                        gap: var(--joy-core-spacing-5);

                        @include mq.screen_xs() {
                            flex-wrap: wrap;
                            gap: var(--joy-core-spacing-2);
                        }

                        &-duration {
                            display: flex;
                            align-items: center;
                            gap: var(--joy-core-spacing-1);
                            white-space: nowrap;

                            .joy-icon {
                                position: relative;
                                top: 1px;
                            }
                        }
                    }

                    &-event {
                        display: flex;
                        flex-direction: column;
                        font-size: var(--joy-font-size-primary-600);
                        font-weight: var(--joy-font-weight-bold);
                        gap: var(--joy-core-spacing-1);

                        &__date {
                            display: flex;
                            align-items: center;
                            gap: var(--joy-core-spacing-5);

                            .joy-icon {
                                position: relative;
                                top: 1px;
                            }

                            @include mq.screen_xs() {
                                flex-wrap: wrap;
                            }
                        }

                        &__event-type {
                            border: 1px solid var(--joy-color-neutral-40);
                            padding: 0 var(--joy-core-spacing-2);
                            border-radius: 20px;
                            display: inline-block;
                        }
                    }
                }

                .excerpt {
                    margin-top: var(--joy-core-spacing-5);

                    p {
                        color: var(--joy-color-neutral-40);
                    }
                }

                .author-info {
                    margin-top: var(--joy-core-spacing-6);
                }
            }
        }
    }
</style>
